import { useEffect, useState } from "react";
import { format } from "date-fns";
import api, { errorMessage } from "./api";
import useAdminStore from "./adminStore";
import { isPrinterOnline } from "./adminLogic";
import PopupsSection from "./PopupsSection";
import { AdminField, Card, Eyebrow, Feedback, Hint, PageTitle, PrimaryButton, SecondaryButton, Toggle } from "./ui";

const EMPTY_PERIOD = { enabled: false, startDate: "", endDate: "", note: "" };

const readSetting = async (key, fallback) => {
    try {
        const response = await api.get(`/settings/${key}`);
        return response.data.value;
    } catch (error) {
        return fallback; // Einstellung existiert noch nicht
    }
};

const saveSetting = (key, value) => api.post("/settings", { key, value });

// Bestell-Schalter: speichern sofort, damit kein älterer Stand das Terminal überschreibt.
const OrderSwitches = () => {
    const printerPing = useAdminStore((state) => state.printerPing);
    const [values, setValues] = useState({ deliveryAvailable: false, ignoreDeliveryState: false });
    const [message, setMessage] = useState(null);

    useEffect(() => {
        Promise.all([readSetting("deliveryAvailable", false), readSetting("ignoreDeliveryState", false)]).then(
            ([deliveryAvailable, ignoreDeliveryState]) =>
                setValues({ deliveryAvailable: Boolean(deliveryAvailable), ignoreDeliveryState: Boolean(ignoreDeliveryState) }),
        );
    }, []);

    const change = async (key, value) => {
        setValues((v) => ({ ...v, [key]: value }));
        setMessage(null);
        try {
            await saveSetting(key, value);
            setMessage({ text: "Gespeichert." });
        } catch (err) {
            setValues((v) => ({ ...v, [key]: !value }));
            setMessage({ error: true, text: errorMessage(err, "Speichern fehlgeschlagen.") });
        }
    };

    const online = isPrinterOnline(printerPing);

    return (
        <Card className="grid gap-1 p-[18px]">
            <Eyebrow className="mb-2">Bestellungen</Eyebrow>
            <Toggle
                label="Lieferung verfügbar"
                description="Aus bei hoher Auslastung – Abholung bleibt möglich"
                checked={values.deliveryAvailable}
                onChange={(v) => change("deliveryAvailable", v)}
            />
            <Toggle
                label="Zeitfenster ignorieren"
                description="Bestellung jederzeit erlauben (Testbetrieb)"
                checked={values.ignoreDeliveryState}
                onChange={(v) => change("ignoreDeliveryState", v)}
            />
            <div className="flex min-h-[52px] items-center justify-between gap-4">
                <span>
                    <span className="block text-base">Bondrucker {online ? "online" : "offline"}</span>
                    <span className="text-[13px] text-forest/55">
                        {printerPing
                            ? `Zuletzt online ${format(new Date(printerPing), "dd.MM. HH:mm:ss")}`
                            : "Noch keine Verbindung"}{" "}
                        · Bestellungen nur bei Verbindung
                    </span>
                </span>
                <span
                    aria-hidden="true"
                    className={`inline-block h-3 w-3 flex-none rounded-full ${online ? "bg-erfolg" : "bg-fehler"}`}
                />
            </div>
            <Feedback message={message} />
        </Card>
    );
};

// Zeitraum mit Schalter, Von/Bis und Hinweis (Betriebsschließung, Sonderöffnung).
const PeriodSetting = ({ settingKey, title, description, noteLabel, notePlaceholder, multilineNote, requireDates }) => {
    const [value, setValue] = useState(EMPTY_PERIOD);
    const [message, setMessage] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        readSetting(settingKey, null).then((v) => setValue({ ...EMPTY_PERIOD, ...(v || {}) }));
    }, [settingKey]);

    const save = async (e) => {
        e.preventDefault();
        setMessage(null);
        if (value.enabled && requireDates && (!value.startDate || !value.endDate)) {
            setMessage({ error: true, text: "Bitte Von- und Bis-Datum angeben." });
            return;
        }
        if (value.startDate && value.endDate && value.endDate < value.startDate) {
            setMessage({ error: true, text: "Das Ende liegt vor dem Beginn." });
            return;
        }
        setBusy(true);
        try {
            await saveSetting(settingKey, {
                enabled: value.enabled,
                startDate: value.startDate,
                endDate: value.endDate,
                note: value.note,
            });
            setMessage({ text: "Gespeichert." });
        } catch (err) {
            setMessage({ error: true, text: errorMessage(err, "Speichern fehlgeschlagen.") });
        } finally {
            setBusy(false);
        }
    };

    return (
        <Card className="p-[18px]">
            <form onSubmit={save} className="grid gap-3">
                <label className="flex items-center justify-between">
                    <Eyebrow>{title}</Eyebrow>
                    <input
                        type="checkbox"
                        aria-label={`${title} aktiv`}
                        checked={value.enabled}
                        onChange={(e) => setValue({ ...value, enabled: e.target.checked })}
                        className="h-[26px] w-[26px] accent-forest"
                    />
                </label>
                <Hint>{description}</Hint>
                <div className="grid grid-cols-2 gap-2.5">
                    <AdminField
                        label="Von"
                        type="date"
                        value={value.startDate}
                        onChange={(e) => setValue({ ...value, startDate: e.target.value })}
                    />
                    <AdminField
                        label="Bis (inkl.)"
                        type="date"
                        value={value.endDate}
                        onChange={(e) => setValue({ ...value, endDate: e.target.value })}
                    />
                </div>
                <AdminField
                    label={noteLabel}
                    as={multilineNote ? "textarea" : "input"}
                    rows={multilineNote ? 3 : undefined}
                    placeholder={notePlaceholder}
                    value={value.note}
                    onChange={(e) => setValue({ ...value, note: e.target.value })}
                />
                <Feedback message={message} />
                <PrimaryButton type="submit" disabled={busy}>
                    {title} speichern
                </PrimaryButton>
            </form>
        </Card>
    );
};

const PasswordSetting = () => {
    const [currentPassword, setCurrentPassword] = useState("");
    const [password, setPassword] = useState("");
    const [message, setMessage] = useState(null);

    const save = async (e) => {
        e.preventDefault();
        setMessage(null);
        try {
            await api.post("/auth/change-password", { currentPassword, newPassword: password });
            setCurrentPassword("");
            setPassword("");
            setMessage({ text: "Passwort geändert." });
        } catch (err) {
            setMessage({ error: true, text: errorMessage(err, "Passwort konnte nicht geändert werden.") });
        }
    };

    return (
        <Card className="p-[18px]">
            <form onSubmit={save} className="grid gap-3">
                <Eyebrow>Passwort</Eyebrow>
                <input
                    type="password"
                    className="admin-input"
                    aria-label="Aktuelles Passwort"
                    placeholder="Aktuelles Passwort"
                    autoComplete="current-password"
                    required
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                />
                <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2.5">
                    <input
                        type="password"
                        className="admin-input"
                        aria-label="Neues Passwort"
                        placeholder="Neues Passwort (mind. 8 Zeichen)"
                        autoComplete="new-password"
                        minLength={8}
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                    />
                    <SecondaryButton type="submit">Ändern</SecondaryButton>
                </div>
                <Feedback message={message} />
            </form>
        </Card>
    );
};

export default function SettingsPage() {
    return (
        <div className="grid gap-3.5">
            <PageTitle>Einstellungen</PageTitle>
            <OrderSwitches />
            <PeriodSetting
                settingKey="closure"
                title="Betriebsschließung"
                description="Sperrt im Zeitraum Reservierungen und Bestellungen, Hinweis erscheint auf der Website. Hat Vorrang vor der Sonderöffnung."
                noteLabel="Hinweis"
                notePlaceholder="z.B. Betriebsferien vom 24.12. bis 26.12."
                multilineNote
                requireDates
            />
            <PeriodSetting
                settingKey="specialOpening"
                title="Sonderöffnung"
                description="Öffnet im Zeitraum auch an Ruhetagen (Dienstag) mit regulären Zeiten und zeigt „Heute geöffnet“."
                noteLabel="Hinweis"
                notePlaceholder="z.B. Wir haben für Sie geöffnet"
            />
            <PopupsSection />
            <PasswordSetting />
        </div>
    );
}
